import { codeUnitString } from "../code-unit-string.js";
import { kernelCanonicalJson, sha256DigestOf } from "../kernel/kernel-encoding.js";
import { z } from "zod";
import { utf8ToBytes } from "@noble/hashes/utils.js";
//#region src/library/library-rights.ts
/**
* Library rights: the rights a shared library declares as data, and the names a consuming app
* asks for (2026-10-12-shared-libraries, D1, D2, I3, I4, R-006, R-018, R-030).
*
* A library right has the name `<library-slug>:<local name>`. The prefix must equal the slug of the
* library that declares it, so no library can declare a right in the name space of another
* (R-006). Each library right binds a set of the library's internal routes and inbound event
* topics. A platform administrator approves a consuming app's request for one right, and the
* approval keys on the library id, the right name and {@link libraryRightBindingDigest} of the
* bound set (R-018, R-030). So a library version that adds a route or a topic to an approved right
* needs a new approval.
*
* Callers: release-engine publish rules (WS-2), the library right requests and decisions (WS-3),
* the certificate issuer (WS-3b), resolution (WS-6), the operator (WS-8, WS-17) and the SDK guards
* (WS-10, WS-11, WS-21). Pure and browser-safe: no Node builtin and no Node global.
*/
/**
* The grammar of a library slug: a lower-case letter, then one or more units, each a lower-case
* letter, a digit, or a `-` and then a letter or a digit. So a slug has at least 2 characters, ends
* with a letter or a digit, and has no `--`. `smoke-lib` matches; `smoke--lib`, `smoke-`, `9lib`
* and `a` do not. Callers check {@link LIBRARY_SLUG_MAX_LENGTH} first.
*
* A library is an app record with `kind = 'library'` (DEF-2), so it shares the slug name space. The
* grammar is the `appId` grammar of `app-manifest.schema.json` with one more rule, no `--`, which
* `app-manifest.schema.json` applies to the `appId` of a `kind: 'library'` manifest. Every slug that
* matches also matches `ID_PART_PATTERN`, so `libraryInstanceSlug` accepts every library slug that
* this grammar accepts.
*/
const LIBRARY_SLUG_PATTERN = /^[a-z](?:[a-z0-9]|-[a-z0-9])+$/;
/** The longest library slug, equal to the `appId` cap of `app-manifest.schema.json`. */
const LIBRARY_SLUG_MAX_LENGTH = 30;
/**
* The grammar of the local part of a library right, the text after the `:`: a lower-case letter,
* then up to 62 characters of `[a-z0-9-]`. `enqueue` and `read-status` match; `Enqueue`, `9x` and
* `a.b` do not.
*/
const LIBRARY_RIGHT_LOCAL_NAME_PATTERN = /^[a-z][a-z0-9-]{0,62}$/;
/**
* The grammar of a whole library right name: a library slug of 2 to 30 characters with no `--`
* ({@link LIBRARY_SLUG_PATTERN}), a `:`, and a local name. `smoke-lib:enqueue` matches;
* `smoke--lib:enqueue` does not. A `--` in the local name stays legal. The pattern does not hold
* the prefix rule; use {@link libraryRightBelongsTo}. Callers check
* {@link LIBRARY_RIGHT_NAME_MAX_LENGTH} first.
*/
const LIBRARY_RIGHT_NAME_PATTERN = /^(?![^:]*--)[a-z][a-z0-9-]{0,28}[a-z0-9]:[a-z][a-z0-9-]{0,62}$/;
/** The longest library right name: a 30-character slug, the `:` and a 63-character local name. */
const LIBRARY_RIGHT_NAME_MAX_LENGTH = 94;
/** The most rights one library version declares in `libraryRights[]`. */
const LIBRARY_RIGHTS_MAX = 32;
/** The most routes one library right binds. */
const LIBRARY_RIGHT_ROUTES_MAX = 32;
/** The longest description of a library right, in UTF-16 code units. */
const LIBRARY_RIGHT_DESCRIPTION_MAX_LENGTH = 512;
/** The longest route path a library right binds. */
const LIBRARY_ROUTE_PATH_MAX_LENGTH = 256;
/**
* The grammar of the fixture id a bound route names: the `fixtureId` grammar of
* `fixture.schema.json`, at most {@link LIBRARY_ROUTE_FIXTURE_MAX_LENGTH} characters.
*/
const LIBRARY_ROUTE_FIXTURE_PATTERN = /^[a-z][a-z0-9-]*$/;
/** The longest fixture id a bound route names, equal to the `fixtureId` cap of `fixture.schema.json`. */
const LIBRARY_ROUTE_FIXTURE_MAX_LENGTH = 24;
/** The HTTP methods a bound route may name: the enum of `$defs.internalExposeRule.methods` in `fixture.schema.json`. */
const LIBRARY_ROUTE_METHODS = Object.freeze([
	"GET",
	"POST",
	"PUT",
	"PATCH",
	"DELETE",
	"HEAD",
	"OPTIONS"
]);
/** The zod form of {@link LibraryRightRoute}. Strict; `.max()` before each `.regex()`. */
const libraryRightRouteSchema = z.strictObject({
	fixture: z.string().max(24).regex(LIBRARY_ROUTE_FIXTURE_PATTERN),
	path: z.string().min(1).max(256).startsWith("/"),
	methods: z.array(z.enum(LIBRARY_ROUTE_METHODS)).min(1).refine((methods) => new Set(methods).size === methods.length, { message: "methods must be unique" }).optional()
});
/** The zod form of one library right name: the length cap, then {@link LIBRARY_RIGHT_NAME_PATTERN}. */
const libraryRightNameSchema = z.string().max(94).regex(LIBRARY_RIGHT_NAME_PATTERN);
/**
* The zod form of {@link LibraryRightDeclaration}. Strict. It refuses a name off the grammar, an
* empty or over-long description, zero routes, more than {@link LIBRARY_RIGHT_ROUTES_MAX} routes,
* and two routes with the same fixture and path. It does not hold the prefix rule, which needs the
* library slug; the publish door holds it with {@link libraryRightBelongsTo}.
*/
const libraryRightDeclarationSchema = z.strictObject({
	name: libraryRightNameSchema,
	description: codeUnitString().min(1).max(512),
	routes: z.array(libraryRightRouteSchema).min(1).max(32).refine((routes) => new Set(routes.map((route) => `${route.fixture} ${route.path}`)).size === routes.length, { message: "two routes name the same fixture and path" })
});
/**
* The zod form of a whole `app.json` `libraryRights[]` list (contracts-WS-0-R10). Each member must
* pass {@link libraryRightDeclarationSchema}. It refuses:
*
* 1. more than {@link LIBRARY_RIGHTS_MAX} (32) rights;
* 2. two rights with the same `name` (path `<index>.name` of the second).
*
* Rule 2 exists because an approval keys on the library id, the right name and the binding digest
* (R-018, R-030). Two declarations of one name would give one name two bound route sets, so an
* approval of one digest could grant the routes of the other. The JSON Schema twin refuses only two
* equal members (`uniqueItems`), so the publish door (WS-2) parses the list with this schema. It
* does not hold the prefix rule or the one-right-per-route rule.
*/
const libraryRightDeclarationListSchema = z.array(libraryRightDeclarationSchema).max(32).superRefine((rights, ctx) => {
	const seen = /* @__PURE__ */ new Set();
	rights.forEach((right, index) => {
		if (seen.has(right.name)) ctx.addIssue({
			code: "custom",
			path: [index, "name"],
			message: `library right ${right.name} is declared twice`
		});
		seen.add(right.name);
	});
});
/** True when `value` is a string within the cap that matches {@link LIBRARY_RIGHT_NAME_PATTERN}. Never throws. Pure. */
function isLibraryRightName(value) {
	return typeof value === "string" && value.length <= 94 && LIBRARY_RIGHT_NAME_PATTERN.test(value);
}
/**
* The library slug of `name`, the text before the `:`, or `null` when `name` is no library right
* name. `libraryRightPrefix('smoke-lib:enqueue')` is `'smoke-lib'`. Never throws. Pure.
*/
function libraryRightPrefix(name) {
	if (!isLibraryRightName(name)) return null;
	return name.slice(0, name.indexOf(":"));
}
/**
* True when `name` is a library right name whose prefix equals `librarySlug` exactly (R-006).
* `libraryRightBelongsTo('smoke-lib:enqueue', 'smoke-lib')` is true; the same name against
* `'smoke'` is false. Never throws. Pure.
*/
function libraryRightBelongsTo(name, librarySlug) {
	return libraryRightPrefix(name) === librarySlug;
}
/**
* The code-point order of two library right names (I4): negative when `a` sorts first, positive
* when `b` does, zero when they are equal. The grammar is ASCII, so code points and UTF-16 code
* units give one order. Pure.
*/
function compareLibraryRightNames(a, b) {
	return a < b ? -1 : a > b ? 1 : 0;
}
/**
* A new list with the members of `names` in code-point order and no duplicate (I4). The input stays
* as it is. It does not check the grammar: a parse after it refuses a bad name. Pure.
*/
function sortLibraryRights(names) {
	return [...new Set(names)].sort(compareLibraryRightNames);
}
/** The most library right names one rights certificate `lrt` claim carries. */
const LIBRARY_RIGHTS_CLAIM_MAX = 64;
/**
* The zod form of the `lrt` claim list: 1 to {@link LIBRARY_RIGHTS_CLAIM_MAX} library right names,
* unique and in code-point order (I4). It never sorts; build the list with
* {@link sortLibraryRights}.
*/
const libraryRightListSchema = z.array(libraryRightNameSchema).min(1).max(64).superRefine((names, ctx) => {
	for (let i = 1; i < names.length; i += 1) if (compareLibraryRightNames(names[i - 1], names[i]) >= 0) {
		ctx.addIssue({
			code: "custom",
			path: [i],
			message: "library rights must be unique and in code-point order"
		});
		return;
	}
});
/**
* The `sha256:` digest that an approval of one library right keys on (R-018, R-030): the bound
* route set and the bound inbound topics of one library version. Equal sets give equal digests
* whatever the input order:
*
* 1. Each route becomes `{ fixture, path, methods }`, with `methods` sorted and unique, or `null`
*    when absent.
* 2. The routes sort by the canonical JSON text of each route, and a duplicate drops.
* 3. The topics sort in code-point order, and a duplicate drops.
* 4. The digest is the SHA-256 of the UTF-8 canonical JSON of `{ v: 1, routes, inboundTopics }`.
*
* An absent `methods` and a list of all seven methods give two digests: the publish door compares
* the declared text, so a change from one to the other needs a new approval. Pure.
*/
function libraryRightBindingDigest(binding) {
	const routes = binding.routes.map((route) => ({
		fixture: route.fixture,
		path: route.path,
		methods: route.methods === void 0 ? null : [...new Set(route.methods)].sort()
	}));
	const canonical = {
		v: 1,
		routes: [...new Set(routes.map((route) => kernelCanonicalJson(route)))].sort().map((text) => JSON.parse(text)),
		inboundTopics: [...new Set(binding.inboundTopics)].sort()
	};
	return sha256DigestOf(utf8ToBytes(kernelCanonicalJson(canonical)));
}
//#endregion
export { LIBRARY_RIGHTS_CLAIM_MAX, LIBRARY_RIGHTS_MAX, LIBRARY_RIGHT_DESCRIPTION_MAX_LENGTH, LIBRARY_RIGHT_LOCAL_NAME_PATTERN, LIBRARY_RIGHT_NAME_MAX_LENGTH, LIBRARY_RIGHT_NAME_PATTERN, LIBRARY_RIGHT_ROUTES_MAX, LIBRARY_ROUTE_FIXTURE_MAX_LENGTH, LIBRARY_ROUTE_FIXTURE_PATTERN, LIBRARY_ROUTE_METHODS, LIBRARY_ROUTE_PATH_MAX_LENGTH, LIBRARY_SLUG_MAX_LENGTH, LIBRARY_SLUG_PATTERN, compareLibraryRightNames, isLibraryRightName, libraryRightBelongsTo, libraryRightBindingDigest, libraryRightDeclarationListSchema, libraryRightDeclarationSchema, libraryRightListSchema, libraryRightNameSchema, libraryRightPrefix, libraryRightRouteSchema, sortLibraryRights };

//# sourceMappingURL=library-rights.js.map